import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../auth/AuthContext';

import { useNavigate } from "react-router-dom";

import { API_BASE_URL } from "../api/config";

import { showSuccess, showError, showInfo, redirectPage } from "../utils/notification";

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [logoutReason, setLogoutReason] = useState('');
  const { isAuthenticated, refresh } = useAuth();

  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
    // Pen-test fix 5.4 — if we were bounced here because the account was
    // signed in from another device, show the reason so the user knows.
    try {
      const reason = sessionStorage.getItem('logout_reason');
      if (reason) {
        setLogoutReason(reason);
        sessionStorage.removeItem('logout_reason');
      }
    } catch {}
  }, [navigate, isAuthenticated]);

  const mapUserTypeToRole = (userType) => {
    if (!userType) return '';
    const t = String(userType).toUpperCase();
    if (t === 'ADMIN') return 'admin';
    if (t === 'DEPT_ADMIN') return 'dept_admin';
    if (t === 'DEPARTMENT') return 'department';
    if (t === 'STATE') return 'state';
    if (t === 'NGO' || t === 'OTHERS') return 'ngo';
    return t.toLowerCase();
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      showError('Please fill all fields');
      return;
    }

    try {
      const payload = {
        email_id: email,
        password: password,
      };

      // Pen-test remediation — login now sets the JWT as an HTTP-only cookie
      // on the server; the browser stores it automatically. We must send
      // `withCredentials` so the cookie is accepted / echoed back.
      const response = await axios.post(
        `${API_BASE_URL}/auth/login`,
        payload,
        { withCredentials: true }
      );

      if (response.data && response.data.success === true) {
        // Pen-test remediation — do not write auth credentials or profile
        // metadata to localStorage. The JWT already lives in the HttpOnly
        // cookie the server set. We ask the AuthContext to (re-)hydrate
        // from /auth/me, then navigate. All UI components read the user
        // through useAuth() instead of localStorage.
        await refresh();
        showSuccess(response.data.message || 'Login successful');
        // Route by role: STATE -> state dashboard, DEPT_ADMIN -> user master,
        // everyone else -> normal dashboard.
        const nextRole = mapUserTypeToRole(response.data.data?.user?.user_type);
        let next = '/dashboard';
        if (nextRole === 'state') next = '/state-dashboard';
        else if (nextRole === 'dept_admin') next = '/dept-admin-dashboard';
        window.location.href = next;
      } else {
        showError(response.data?.message || 'Login failed');
      }
    } catch (error) {
      showError(error?.response?.data?.message || 'Login failed');
    }
  };

  const handleRegister = () => {
    window.location.href = '/register';
  };

  return (
    <div className="h-screen bg-gradient-to-br from-blue-50 to-blue-100 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="w-full px-4 py-3 flex-shrink-0">
        <div className="flex items-center justify-center">
          <div className="flex items-center space-x-3 sm:space-x-4">
            <div className="w-8 h-8 sm:w-10 sm:h-10 flex-shrink-0">
              <img src="/images/tnlogo.png" alt="Tamil Nadu Government Logo" className="w-full h-full object-contain" />
            </div>
            <div className="text-center sm:text-left">
              <h1 className="text-sm sm:text-base font-bold text-[#036FAA] font-['Poppins'] leading-tight">Tamil Nadu Government</h1>
              <h2 className="text-xs text-[#036FAA] font-medium font-['Poppins'] leading-tight">NOC Application System</h2>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4 pb-3 overflow-hidden">
        <div className="w-full max-w-sm sm:max-w-md md:max-w-3xl lg:max-w-4xl h-full max-h-full flex items-center justify-center">

          {/* Mobile Layout */}
          <div className="md:hidden w-full">
            <div className="bg-white rounded-xl shadow-lg overflow-hidden max-h-full flex flex-col">
              <div className="bg-gradient-to-br from-[#036FAA] to-[#025580] p-4 flex items-center justify-center flex-shrink-0">
                <div className="bg-white backdrop-blur-sm rounded-2xl p-3 w-20 h-20 sm:w-24 sm:h-24 flex items-center justify-center">
                </div>
              </div>

              <div className="p-4 flex-1 overflow-y-auto">
                <h2 className="text-lg sm:text-xl font-bold text-[#036FAA] mb-4 text-center">Login</h2>

                {logoutReason && (
                  <div className="mb-3 p-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded">
                    {logoutReason}
                  </div>
                )}

                <div className="space-y-3">
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-1">Email ID</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-blue-200 rounded focus:outline-none focus:ring-1 focus:ring-[#036FAA] focus:border-transparent transition duration-200"
                      placeholder="Enter your email"
                    />
                  </div>

                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-1">Password</label>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-blue-200 rounded focus:outline-none focus:ring-1 focus:ring-[#036FAA] focus:border-transparent transition duration-200"
                      placeholder="Enter your password"
                    />
                  </div>

                  <button
                    onClick={handleLogin}
                    className="w-full bg-gradient-to-r from-[#036FAA] to-[#025580] text-white py-2.5 px-4 rounded hover:from-[#025580] hover:to-[#014060] focus:outline-none focus:ring-2 focus:ring-[#036FAA] focus:ring-offset-1 font-medium text-sm transition duration-200 transform hover:scale-[1.01] active:scale-[0.99]"
                  >
                    Login
                  </button>
                </div>

                <div className="mt-4 text-center">
                  <p className="text-[#036FAA] text-sm mb-1">New NGO / Organization?</p>
                  <button
                    onClick={handleRegister}
                    className="text-[#036FAA] hover:text-[#025580] font-medium text-sm underline decoration-1 underline-offset-2 hover:decoration-[#025580] transition duration-200"
                  >
                    Register here →
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Desktop Layout */}
          <div className="hidden md:block w-full">
            <div className="bg-white rounded-xl shadow-lg overflow-hidden grid md:grid-cols-2 h-[85vh] max-h-[600px] lg:max-h-[650px]">

              <div className="bg-gradient-to-br from-[#036FAA] to-[#025580] p-6 lg:p-8 flex items-center justify-center relative">
                <div className="absolute inset-0 bg-[#036FAA] opacity-10"></div>
                <div className="relative z-10 text-center">
                  <div className="bg-white backdrop-blur-sm rounded-3xl p-6 border border-white/30 w-40 h-40 lg:w-48 lg:h-48 flex flex-col items-center justify-center">
                    <div className="flex justify-center mb-4">
                      <img src="/images/login.png" alt="Government Illustration" className="w-full h-full object-contain rounded-2xl" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-6 lg:p-8 flex flex-col justify-center">
                <div className="max-w-xs mx-auto w-full">
                  <h2 className="text-2xl font-bold text-[#036FAA] mb-6 text-center">Login</h2>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-gray-700 text-sm font-medium mb-1">Email ID</label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-blue-200 rounded focus:outline-none focus:ring-1 focus:ring-[#036FAA] focus:border-transparent transition duration-200"
                        placeholder="Enter your email"
                      />
                    </div>

                    <div>
                      <label className="block text-gray-700 text-sm font-medium mb-1">Password</label>
                      <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-blue-200 rounded focus:outline-none focus:ring-1 focus:ring-[#036FAA] focus:border-transparent transition duration-200"
                        placeholder="Enter your password"
                      />
                    </div>

                    <button
                      onClick={handleLogin}
                      className="w-full bg-gradient-to-r from-[#036FAA] to-[#025580] text-white py-2.5 px-4 rounded hover:from-[#025580] hover:to-[#014060] focus:outline-none focus:ring-2 focus:ring-[#036FAA] focus:ring-offset-1 font-medium text-sm transition duration-200 transform hover:scale-[1.01]"
                    >
                      Login
                    </button>
                  </div>

                  <div className="mt-6 text-center">
                    <p className="text-[#036FAA] text-sm mb-2">New NGO / Organization?</p>
                    <button
                      onClick={handleRegister}
                      className="text-[#036FAA] hover:text-[#025580] font-medium text-sm underline decoration-1 underline-offset-2 hover:decoration-[#025580] transition duration-200"
                    >
                      Register here →
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
